import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/**
 * MobileBackButton — compact "go back" button shown on mobile only.
 *
 * Rules:
 *  - Hidden on desktop (md+) — those users already have browser-native back.
 *  - Hidden on auth-entry surfaces where "back" makes no sense:
 *      /, /login*, /register
 *  - Uses `navigate(-1)` when history exists; falls back to `/` otherwise.
 *  - Arrow direction respects RTL: pointing right when Arabic, left otherwise.
 */
const HIDDEN_PATHS = [
  '/',
  '/login',
  '/register',
];

export default function MobileBackButton() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, localize } = useLanguage();
  const isAr = language === 'ar';

  // Hide on home / top-level auth surfaces
  const path = location.pathname || '/';
  const hidden =
    HIDDEN_PATHS.some((p) => path === p) ||
    path.startsWith('/login') ||
    path.startsWith('/register');
  if (hidden) return null;

  const handleBack = () => {
    // `window.history.length` starts at 1 on fresh tab; 2+ means real history
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/', { replace: true });
    }
  };

  return (
    <button
      type="button"
      onClick={handleBack}
      aria-label={localize({ ar: 'رجوع', en: 'Go back' })}
      data-testid="mobile-back-button"
      className={`md:hidden fixed z-[55] top-3 ${isAr ? 'right-3' : 'left-3'}
        h-10 px-3 rounded-full inline-flex items-center gap-1.5
        bg-white/15 text-white backdrop-blur-2xl
        ring-1 ring-white/30 shadow-[0_4px_18px_rgba(15,23,42,0.35)]
        hover:bg-white/25 hover:ring-indigo-300/60 active:scale-95
        transition-all`}
      style={{ WebkitBackdropFilter: 'blur(24px)' }}
    >
      <ChevronLeft className={`w-5 h-5 text-indigo-200 ${isAr ? 'rotate-180' : ''}`} strokeWidth={2.6} />
      <span className="text-[12px] font-bold tracking-wide">
        {localize({ ar: 'رجوع', en: 'Back' })}
      </span>
    </button>
  );
}
